/**
 * Hook for submitting verification code API request
 */
import { useMemo } from "react";
import { useSignup as useSignupContext } from "../../SignupContext";
import { handleApiError, UserAlreadyExistsError, InvalidStateError, } from "../../utils/Errors";
import { useApiRequest } from "../../utils/useApiRequest";
/**
 * Hook for managing verification code submission API request
 * @returns RequestState object for submitting verification code
 */
export function useSubmitVerificationCode() {
    const { apiClient, formValues } = useSignupContext();
    const { state: submitVerificationCodeState, execute: executeSubmitVerificationCode, reset: resetSubmitVerificationCodeState, } = useApiRequest();
    return useMemo(() => ({
        execute: async () => {
            // Validate required fields
            const missingFields = [];
            if (!formValues.phoneNumber) {
                missingFields.push("phoneNumber");
            }
            if (!formValues.verificationCode) {
                missingFields.push("verificationCode");
            }
            if (!formValues.email) {
                missingFields.push("email");
            }
            if (missingFields.length > 0) {
                throw new InvalidStateError("Cannot submit verification code with invalid or incomplete data", missingFields);
            }
            return executeSubmitVerificationCode(async (params) => {
                const response = await apiClient.confirmMobileVerification(params);
                if (response.ok) {
                    const data = await response.json();
                    // Update the API client with the token for subsequent authenticated requests
                    apiClient.setAccessToken(data.token);
                    if (data.authAction === "SIGNUP") {
                        return { authAction: data.authAction, token: data.token };
                    }
                    else {
                        throw new UserAlreadyExistsError(data.token, data.authAction);
                    }
                }
                else {
                    const error = await handleApiError(response);
                    throw error;
                }
            }, {
                phoneNumber: {
                    number: formValues.phoneNumber,
                    countryIso2: "MX", // TODO: Make this configurable
                },
                code: formValues.verificationCode,
                email: formValues.email,
            });
        },
        isLoading: submitVerificationCodeState.isLoading,
        error: submitVerificationCodeState.error,
        data: submitVerificationCodeState.data,
        reset: resetSubmitVerificationCodeState,
    }), [
        apiClient,
        executeSubmitVerificationCode,
        formValues.phoneNumber,
        formValues.verificationCode,
        formValues.email,
        submitVerificationCodeState,
        resetSubmitVerificationCodeState,
    ]);
}
